import type { ComponentType, ReactNode } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { ConversationStarter } from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import { type OptionType } from '@atlaskit/select';
import { type Value } from '@atlaskit/smart-user-picker';
import { type AgentAction } from '@atlassian/conversation-assistant-service-api/types/agent-actions';
import {
	type Agent,
	type AgentKnowledgeConfiguration,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

export type ConfluenceFilterSelectType = OptionType & {
	value: 'all' | 'spaces' | 'pages';
};

export type KnowledgeFilterSelectType = OptionType & {
	ari: string;
	iconUrl?: string;
	renderIcon?: () => React.ReactNode;
	spaceKey?: string;
	url: string;
};

export type AgentFormFields = {
	name: string;
	description: string;
	instructions: string;
	conversationStarters: string[];
	agentActions: AgentAction[];
	creator: Value;
	knowledgeScope: boolean;
	confluenceFilterScope?: ConfluenceFilterSelectType;
	confluenceAncestorPageFilters?: KnowledgeFilterSelectType[];
	confluenceSpaceFilters?: KnowledgeFilterSelectType[];
	jiraProjectFilters?: KnowledgeFilterSelectType[];
	jsmProjectFilters?: KnowledgeFilterSelectType[];
	knowledgeEnabledFields: KnowledgeEnabledFields;
};

export type AgentFormFieldsManualSetup = Pick<
	AgentFormFields,
	'name' | 'description' | 'instructions' | 'conversationStarters'
>;

type KnowledgeEnabledFields = {
	[K in AgentKnowledgeConfiguration['source']]?: boolean;
};

export type ValidAgentFormFields = keyof AgentFormFields;

export const isSystemAgent = (agent: Agent): boolean => agent.creator_type === 'SYSTEM';
export const isCustomerAgent = (agent: Agent): boolean => agent.creator_type === 'CUSTOMER';
// BE will deprecate THIRD_PARTY and use FORGE instead
/**
 * @private
 * @deprecated Use isForgeAgentByCreatorType from @atlaskit/rovo-agent-components/common/utils/is-forge-agent instead
 * @param agent
 * @returns boolean
 */
export const isForgeAgent = (agent: Agent): boolean =>
	agent.creator_type === 'THIRD_PARTY' ||
	agent.creator_type === 'FORGE' ||
	(agent.creator_type === 'REMOTE_A2A' && fg('rovo_agent_support_a2a_avatar'));
export const isPrivateAgent = (agent: Agent): boolean => agent.visibility === 'PRIVATE';
export const isOOTBAgent = (agent: Agent): boolean => agent.creator_type === 'OOTB';
export const BrowseCategories = {
	ALL_AGENTS: 'all-agents',
	STARRED_AGENTS: 'starred-agents',
	MY_AGENTS: 'my-agents',
	TEMPLATES: 'templates',
	VERIFIED_AGENTS: 'verified-agents',
	/** Jira browse tab: agents available for Jira (UI label localized in product). */
	AVAILABLE_FOR_JIRA: 'available-for-jira',
} as const;
export type BrowseCategory = (typeof BrowseCategories)[keyof typeof BrowseCategories];

export type AgentTabs =
	| 'all-agents'
	| 'starred-agents'
	| 'my-agents'
	| 'templates'
	| 'verified-agents'
	| 'available-for-jira';

export type UIContext = 'agents-modal' | 'agents-sidebar' | 'agents-fullscreen';

export type CustomAgentCardMenuOption = {
	id: string;
	label: string;
	onClick: (agent: Agent) => void;
};

export type AgentRendererProps = {
	agentAri?: string | null;
	canEditAgent?: boolean;
	agentIsAssignableInJira?: boolean | null;
};

/**
 * Props passed to product-injected browse modal tab content (e.g. Jira “Available for Jira”).
 */
export type CustomTabContentProps = {
	searchValue: string;
	onSearch?: (value: string) => void;
	/** Switch the modal sidebar tab (e.g. product empty-state “See all agents” → All). */
	onTabChange: (tab: BrowseCategory) => void;
	customTabId: BrowseCategory;
	siteId: string;
	onClose: () => void;
} & AssistanceServiceInputParam & {
		onClickChat: (agent: Agent) => void;
		onClickAgent?: (agent: Agent) => void;
		handleClickConversationStarter: (
			agent: Agent,
			conversationStarter: ConversationStarter,
		) => void;
		/** Opens agent delete / confirm flow (same as default browse list). */
		onDeleteAgent?: (agent: Agent, source: string) => void;
		agentCardRole?: 'link' | 'button';
		getAgentCardAriaLabel?: (agentName: string, agentJiraContext?: AgentRendererProps) => string;
		customAgentDropdownMenuOptions?: CustomAgentCardMenuOption[];
		renderAgentCardFooter?: (props: AgentRendererProps) => ReactNode;
		enableJiraAgentFields?: boolean;
	};

export type CustomTabConfig = {
	label: string;
	/** When set, overrides the default search placeholder for the modal main column while this tab is selected. */
	searchPlaceholder?: string;
	Content: ComponentType<CustomTabContentProps>;
};

export type CustomTabsConfig = Partial<Record<BrowseCategory, CustomTabConfig>>;

type BrowseAgentsModalBaseProps = {
	onClose: () => void;
	siteId: string;
	onClickChat: (agent: Agent) => void;
	onClickAgent?: (agent: Agent) => void;
	handleClickConversationStarter: (agent: Agent, conversationStarter: ConversationStarter) => void;
	browseCategory?: BrowseCategory;
	agentTabs?: AgentTabs[];
	agentCardRole?: 'link' | 'button';
	getAgentCardAriaLabel?: (agentName: string, agentJiraContext?: AgentRendererProps) => string;
	customAgentDropdownMenuOptions?: CustomAgentCardMenuOption[];
	wrapInModal?: boolean;
	/**
	 * Optional product-specific tabs keyed by {@link BrowseCategory} id.
	 * Jira passes this only when `jira_browse_agents_modal` is on; {@link BrowseAgentTabs} also checks the same gate.
	 */
	customTabs?: CustomTabsConfig;
} & AssistanceServiceInputParam;

export type BrowseAgentsModalProps = BrowseAgentsModalBaseProps &
	(
		| {
				enableJiraAgentFields: true;
				renderAgentCardFooter?: (props: AgentRendererProps) => ReactNode;
				renderAgentProfileCta?: (props: AgentRendererProps) => ReactNode;
				renderAgentProfileAdditionalContent?: (props: AgentRendererProps) => ReactNode;
		  }
		| {
				enableJiraAgentFields?: false;
				renderAgentCardFooter?: (props: Pick<AgentRendererProps, 'agentAri'>) => ReactNode;
				renderAgentProfileCta?: (props: Pick<AgentRendererProps, 'agentAri'>) => ReactNode;
				renderAgentProfileAdditionalContent?: (
					props: Pick<AgentRendererProps, 'agentAri'>,
				) => ReactNode;
		  }
	);

type BrowseAgentsExtendedProps = {
	chatRefreshHeader?: ReactNode;
	singleColumn?: boolean;
};

export type BrowseAgentsSidebarProps = BrowseAgentsModalProps &
	BrowseAgentsExtendedProps & {
		titleElement: ReactNode;
	};

export type BrowseAgentsModalPropsWithoutOnClose = Omit<BrowseAgentsModalProps, 'onClose'>;

export type BrowseAgentsFullscreenProps = Omit<
	BrowseAgentsModalPropsWithoutOnClose & BrowseAgentsExtendedProps,
	'chatRefreshHeader'
>;

/**
 * Extension of the Agent business type with client-side-only fields
 * used for placeholder agents pending full hydration from the backend.
 */
export type ClientAgent = Agent & {
	/**
	 * Client-side only: marks the agent as a placeholder with partial data,
	 * pending full hydration from the backend.
	 */
	isPlaceholder?: boolean;
	/**
	 * Client-side only: temporary avatar URL for placeholder agents,
	 * used to avoid avatar flicker during hydration.
	 */
	placeholderAvatarUrl?: string;
};
